/**
 * SRA-Compliant Risk Assessment Panel
 * 
 * Part 4.3 of the FORF form - Risk Assessment scoring with 8 factors.
 * Implements points-based scoring with weighted factors and real-time band calculation.
 * 
 * Features:
 * - Real-time score calculation as user selects L/M/H ratings
 * - Visual display of calculated risk band
 * - Submission blocking/warning based on risk level
 * - Supervisor override UI (when authorized)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { API } from '../../config/api';
import axios from "axios";
import { errorMessage } from "../../utils/errorMessage";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info,
  Lock,
  UserCheck,
  HelpCircle,
  ShieldAlert
} from "lucide-react";
import { InfoTooltip } from "../../components/InfoTooltip";

// Risk level badge colors
const RISK_BADGE_STYLES = {
  Low: "bg-teal-dim text-teal border-teal",
  Medium: "bg-status-amber-dim text-status-amber border-amber-300",
  High: "bg-status-red-dim text-status-red border-red-300"
};

const RISK_BANNER_STYLES = {
  Low: "bg-teal-dim border-teal text-teal",
  Medium: "bg-status-amber-dim border-amber-200 text-status-amber",
  High: "bg-status-red-dim border-red-200 text-status-red"
};

// Factor descriptions for tooltips
const FACTOR_DESCRIPTIONS = {
  complexity: "Legal complexity, number of parties, unusual provisions, cross-border elements",
  value: "Financial exposure relative to fee income, high-value transactions",
  client_vulnerability: "Consumer client, language barriers, unrepresented parties, vulnerable adults",
  regulatory: "AML concerns, sanctions, PEP status, regulatory scrutiny (SRA sensitive)",
  conflict_risk: "Multiple clients, former client relationships, competing interests (SRA sensitive)",
  time_pressure: "Tight deadlines, penalty clauses, exchange dates, urgent completion",
  insurance: "Potential PII exposure, high value undertakings, gaps in cover (SRA sensitive)",
  funding: "Source of funds concerns, delayed payments, third-party funding"
};

// Weighted factor indicator
const WEIGHTED_FACTORS = new Set(["regulatory", "conflict_risk", "insurance"]);

export default function RiskAssessmentPanel({
  riskFactors = {},
  onRiskChange,
  calculatedBand,
  overrideBand,
  overrideReason,
  overrideBy,
  isSupervisor = false,
  isReadOnly = false,
  submissionId = null,
  matterRef = null,
  currentUserEmail = null,
  currentUserName = null,
  onRoutedToMlco = null
}) {
  const [factors, setFactors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [showOverride, setShowOverride] = useState(false);
  const [supervisorCheck, setSupervisorCheck] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    band: "",
    reason: ""
  });
  const [localRiskFactors, setLocalRiskFactors] = useState(riskFactors);

  // Brief 14c-i — MLCO routing state.
  // Debounce timestamp lives in a ref so unrelated re-renders never
  // reset the 60s window (brief §11 #3).
  const [mlcoSubmitting, setMlcoSubmitting] = useState(false);
  const [mlcoRouted, setMlcoRouted] = useState(false);
  const lastMlcoSubmitRef = useRef(0);
  const MLCO_DEBOUNCE_MS = 60_000;

  // Check if current user is a supervisor
  useEffect(() => {
    const checkSupervisor = async () => {
      if (!currentUserEmail) return;
      try {
        const res = await axios.get(`${API}/risk/check-supervisor/${encodeURIComponent(currentUserEmail)}`);
        setSupervisorCheck(res.data);
      } catch (err) {
        console.error("Failed to check supervisor status:", err);
      }
    };
    checkSupervisor();
  }, [currentUserEmail]);

  // Determine if user can override (prop or API check)
  const canOverride = isSupervisor || supervisorCheck?.can_override;

  // Load risk factor definitions
  useEffect(() => {
    const loadFactors = async () => {
      try {
        const res = await axios.get(`${API}/risk/factors`);
        setFactors(res.data);
      } catch (err) {
        console.error("Failed to load risk factors:", err);
        toast.error("Failed to load risk factors");
      } finally {
        setLoading(false);
      }
    };
    loadFactors();
  }, []);

  // Sync local state with props
  useEffect(() => {
    setLocalRiskFactors(riskFactors);
  }, [riskFactors]);

  // Calculate risk whenever factors change
  const calculateRisk = useCallback(async (ratings) => {
    if (Object.keys(ratings).length === 0) return;
    
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/risk/calculate`, ratings);
      setAssessment(res.data);
      // Notify parent of the calculated band
      if (onRiskChange) {
        onRiskChange({
          factors: ratings,
          calculated_band: res.data.calculated_band,
          total_score: res.data.total_score,
          triggers: res.data.triggers
        });
      }
    } catch (err) {
      console.error("Risk calculation failed:", err);
    } finally {
      setCalculating(false);
    }
  }, [onRiskChange]);

  // Debounced calculation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(localRiskFactors).some(v => v)) {
        calculateRisk(localRiskFactors);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localRiskFactors, calculateRisk]);

  // Handle factor rating change
  const handleFactorChange = (factorId, rating) => {
    const updated = { ...localRiskFactors, [factorId]: rating };
    setLocalRiskFactors(updated);
  };

  // Handle supervisor override
  const handleOverrideSubmit = async () => {
    if (!overrideForm.band || overrideForm.reason.length < 10) {
      toast.error("Please select a risk band and provide a reason (min 10 characters)");
      return;
    }

    if (!submissionId) {
      toast.error("Cannot save override - submission ID missing");
      return;
    }

    try {
      await axios.post(`${API}/risk/submission/${submissionId}/override`, {
        override_risk_band: overrideForm.band,
        override_reason: overrideForm.reason,
        override_by: currentUserName || "Supervisor",
        override_by_email: currentUserEmail || ""
      });
      toast.success("Risk band override saved");
      setShowOverride(false);
    } catch (err) {
      toast.error("Failed to save override: " + errorMessage(err, "Failed to save override"));
    }
  };

  // Brief 14c-i — POST chain into universal compliance collections.
  // Mid-chain failure surfaces a toast but the canonical risk-assessment
  // save is unaffected. 60s debounce via lastMlcoSubmitRef.
  const handleSubmitForMlcoReview = useCallback(async () => {
    const now = Date.now();
    if (now - lastMlcoSubmitRef.current < MLCO_DEBOUNCE_MS) return;
    lastMlcoSubmitRef.current = now;

    const band = assessment?.calculated_band;
    if (band !== "High" && band !== "Critical") return;
    if (!submissionId && !matterRef) {
      toast.error("Cannot submit — missing matter reference");
      return;
    }
    if (!currentUserEmail) {
      toast.error("Cannot submit — missing user email");
      return;
    }

    setMlcoSubmitting(true);
    const headers = { "X-Bonsai-User-Email": currentUserEmail };
    const matterId = matterRef || submissionId;
    try {
      const reviewRes = await axios.post(
        `${API}/compliance/matter-reviews`,
        {
          matter_id: matterId,
          review_type: "intake_risk_assessment",
          created_by_email: currentUserEmail,
          intake_submission_id: submissionId || matterId,
          notes: "Submitted via FE intake risk assessment",
        },
        { headers }
      );
      const reviewId = reviewRes.data?.id;

      const highFactors = Object.entries(localRiskFactors || {})
        .filter(([, v]) => (v || "").toLowerCase() === "high")
        .map(([k]) => k);
      for (const factorId of highFactors) {
        await axios.post(
          `${API}/compliance/matter-review-findings`,
          {
            matter_review_id: reviewId,
            severity: "material",
            category: "intake_risk_factor",
            summary: `Factor '${factorId}' rated High at intake.`,
            evidence: JSON.stringify({ factor_id: factorId, rating: "high" }),
            created_by_email: currentUserEmail,
          },
          { headers }
        );
      }

      await axios.post(
        `${API}/compliance/matter-risk-ratings`,
        {
          matter_id: matterId,
          band: (band || "").toLowerCase(),
          score: Number(assessment?.total_score || 0),
          factor_breakdown: { ...(localRiskFactors || {}) },
          calculated_by_email: currentUserEmail,
          trigger: "intake",
        },
        { headers }
      );

      setMlcoRouted(true);
      toast.success("Submitted to MLCO review queue");
      try {
        sessionStorage.setItem(
          "intake_routed_to_mlco",
          JSON.stringify({ matterRef: matterId, band, ts: Date.now() })
        );
      } catch (storageErr) {
        // nosilent: sessionStorage write — banner is best-effort
        console.warn("Could not persist routing banner flag:", storageErr);
      }
      if (onRoutedToMlco) onRoutedToMlco({ matterRef: matterId, reviewId, band });
    } catch (err) {
      console.error("MLCO routing failed:", err);
      toast.error(
        "Could not submit to MLCO. Risk assessment saved locally; please contact MLCO directly."
      );
    } finally {
      setMlcoSubmitting(false);
    }
  }, [
    assessment,
    submissionId,
    matterRef,
    currentUserEmail,
    localRiskFactors,
    onRoutedToMlco,
  ]);

  // Determine final risk band
  const finalBand = overrideBand || assessment?.calculated_band || calculatedBand;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
        <span className="ml-2 text-txt-muted">Loading risk assessment...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-teal flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Part 4.3 – Risk Assessment
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Rate each risk factor. The overall risk band is calculated automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {calculating && <Loader2 className="w-4 h-4 animate-spin text-teal" />}
          {assessment && (
            <span className="text-xs text-slate-400">
              Score: {assessment.total_score}
            </span>
          )}
        </div>
      </div>

      {/* Risk Factors Grid */}
      <div className="grid grid-cols-1 gap-3">
        {factors?.factors?.map((factor) => (
          <RiskFactorRow
            key={factor.id}
            factor={factor}
            rating={localRiskFactors[factor.id] || ""}
            onChange={(rating) => handleFactorChange(factor.id, rating)}
            isWeighted={WEIGHTED_FACTORS.has(factor.id)}
            description={FACTOR_DESCRIPTIONS[factor.id]}
            isReadOnly={isReadOnly}
            score={assessment?.factor_scores?.[factor.id]?.score}
          />
        ))}
      </div>

      {/* Calculated Risk Band Display */}
      {assessment && (
        <div className="p-4 rounded-lg border-2 border-dashed border-navy-border bg-navy-card/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-txt-muted">Calculated Risk:</span>
                <span className={`px-3 py-1 rounded-md text-sm font-semibold border ${RISK_BADGE_STYLES[assessment.calculated_band]}`}>
                  {assessment.calculated_band}
                </span>
              </div>
              
              {overrideBand && overrideBand !== assessment.calculated_band && (
                <>
                  <span className="text-slate-400">→</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-txt-muted">Final (Override):</span>
                    <span className={`px-3 py-1 rounded-md text-sm font-semibold border ${RISK_BADGE_STYLES[overrideBand]}`}>
                      {overrideBand}
                    </span>
                    <div className="group relative">
                      <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 w-64 p-2 bg-slate-800 rounded text-xs text-white">
                        Overridden by {overrideBy}: {overrideReason}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {assessment.high_count} High · {assessment.medium_count} Med · {assessment.low_count} Low
              </span>
            </div>
          </div>

          {/* Trigger explanations */}
          {assessment.triggers?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-navy-border">
              <p className="text-xs text-slate-400 mb-1">Risk triggers:</p>
              <ul className="text-xs text-slate-500 space-y-0.5">
                {assessment.triggers.map((t, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-slate-400">•</span> {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Risk-based Warnings/Blockers */}
      {finalBand === "High" && !overrideBand && (
        <div className={`p-4 rounded-lg border-2 ${RISK_BANNER_STYLES.High}`} data-testid="high-risk-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-status-red flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">High Risk Matter – Supervisor Review Required</p>
              <p className="text-sm mt-1">
                This matter cannot be completed until a supervisor or compliance officer reviews
                the risk assessment and provides an override confirmation.
              </p>
              {!isReadOnly && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {/* BM-H593: the override posts to
                      /api/risk/submission/{submissionId}/override, so it cannot
                      work without a submission. Hidden, not broken, when the
                      panel is mounted with no submissionId (FORF Section 2's
                      shared CmraQuestions, the old matter-page panel). */}
                  {canOverride && submissionId && (
                    <Button
                      onClick={() => setShowOverride(true)}
                      className="bg-status-red hover:bg-status-red text-white"
                      size="sm"
                      data-testid="supervisor-override-btn"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Provide Supervisor Override
                    </Button>
                  )}
                  <Button
                    onClick={handleSubmitForMlcoReview}
                    disabled={mlcoSubmitting || mlcoRouted}
                    className="bg-status-red hover:bg-status-red text-white"
                    size="sm"
                    data-testid="submit-for-mlco-review-btn"
                  >
                    {mlcoSubmitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldAlert className="w-4 h-4 mr-2" />
                    )}
                    {mlcoRouted ? "Submitted to MLCO" : "Submit for MLCO Review"}
                  </Button>
                </div>
              )}
              {mlcoRouted && (
                <p
                  className="text-xs mt-2 text-status-red"
                  data-testid="mlco-routed-inline-confirmation"
                >
                  This matter has been routed to MLCO for review.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {finalBand === "Medium" && (
        <div className={`p-4 rounded-lg border ${RISK_BANNER_STYLES.Medium}`} data-testid="medium-risk-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-status-amber flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Medium Risk – Supervisor Review Recommended</p>
              <p className="text-sm mt-1">
                This matter has been assessed as medium risk. Consider requesting supervisor
                review before proceeding. This matter will appear in the Directors Lounge.
              </p>
            </div>
          </div>
        </div>
      )}

      {finalBand === "Low" && (
        <div className={`p-4 rounded-lg border ${RISK_BANNER_STYLES.Low}`} data-testid="low-risk-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Low Risk</p>
              <p className="text-sm mt-1">
                This matter has been assessed as low risk. Standard procedures apply.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor Override Modal */}
      {showOverride && canOverride && submissionId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowOverride(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-teal" />
              Supervisor Risk Override
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Calculated Band</Label>
                <p className={`mt-1 px-3 py-1.5 rounded text-sm font-medium inline-block ${RISK_BADGE_STYLES[assessment?.calculated_band || "Medium"]}`}>
                  {assessment?.calculated_band || "Not calculated"}
                </p>
              </div>

              <div>
                <Label className="text-slate-700">Override to Risk Band</Label>
                <Select value={overrideForm.band} onValueChange={(v) => setOverrideForm(f => ({ ...f, band: v }))}>
                  <SelectTrigger className="mt-1" data-testid="override-band-select">
                    <SelectValue placeholder="Select risk band" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-700">Reason for Override *</Label>
                <Textarea
                  value={overrideForm.reason}
                  onChange={(e) => setOverrideForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Explain why the risk band is being overridden (min 10 characters)..."
                  rows={3}
                  className="mt-1"
                  data-testid="override-reason-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {overrideForm.reason.length}/10 characters minimum
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowOverride(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleOverrideSubmit}
                  className="bg-teal hover:bg-teal-hover text-white"
                  disabled={!overrideForm.band || overrideForm.reason.length < 10}
                  data-testid="override-submit-btn"
                >
                  Confirm Override
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Individual risk factor row component
function RiskFactorRow({ factor, rating, onChange, isWeighted, description, isReadOnly, score }) {
  return (
    <div className="flex items-center justify-between p-3 bg-navy-hover/60 rounded-lg border border-navy-border hover:border-navy-border transition-colors">
      <div className="flex-1 mr-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-txt text-sm">
            {factor.index}. {factor.label}
          </span>
          {isWeighted && (
            <span className="text-[10px] px-1.5 py-0.5 bg-purple-900/50 text-purple-300 rounded border border-purple-500/30">
              SRA SENSITIVE
            </span>
          )}
          {rating && score !== undefined && (
            <span className="text-xs text-slate-500 ml-1">
              (+{score})
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-0.5">{description}</p>
      </div>
      
      <div className="flex gap-1.5">
        {["low", "medium", "high"].map((level) => {
          const isSelected = rating === level;
          const baseStyle = "px-3 py-1.5 rounded text-xs font-medium border transition-all";
          const levelStyles = {
            low: isSelected 
              ? "bg-teal-dim text-teal border-teal ring-2 ring-teal ring-offset-1 ring-offset-[#1e293b]"
              : "bg-navy-card text-slate-400 border-navy-border hover:border-teal/50 hover:text-teal",
            medium: isSelected
              ? "bg-status-amber-dim text-status-amber border-amber-300 ring-2 ring-amber-400 ring-offset-1 ring-offset-[#1e293b]"
              : "bg-navy-card text-slate-400 border-navy-border hover:border-amber-500/50 hover:text-status-amber",
            high: isSelected
              ? "bg-status-red-dim text-status-red border-red-300 ring-2 ring-red-400 ring-offset-1 ring-offset-[#1e293b]"
              : "bg-navy-card text-slate-400 border-navy-border hover:border-red-500/50 hover:text-status-red"
          };
          
          return (
            <button
              key={level}
              onClick={() => !isReadOnly && onChange(level)}
              disabled={isReadOnly}
              className={`${baseStyle} ${levelStyles[level]} ${isReadOnly ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              data-testid={`risk-${factor.id}-${level}`}
            >
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Export hook for external use
export function useRiskAssessment(submissionId) {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAssessment = useCallback(async () => {
    if (!submissionId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/risk/submission/${submissionId}`);
      setAssessment(res.data);
    } catch (err) {
      console.error("Failed to load risk assessment:", err);
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  const saveAssessment = useCallback(async (factors) => {
    if (!submissionId) return null;
    try {
      const res = await axios.post(`${API}/risk/submission/${submissionId}/assess`, factors);
      setAssessment(res.data.assessment);
      return res.data;
    } catch (err) {
      console.error("Failed to save risk assessment:", err);
      throw err;
    }
  }, [submissionId]);

  const checkCanSubmit = useCallback(async () => {
    if (!submissionId) return { allowed: true };
    try {
      const res = await axios.get(`${API}/risk/submission/${submissionId}/can-submit`);
      return res.data;
    } catch (err) {
      console.error("Failed to check submission status:", err);
      return { allowed: false, error: err.message };
    }
  }, [submissionId]);

  return { assessment, loading, loadAssessment, saveAssessment, checkCanSubmit };
}
